import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { nonprofitsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Loader2,
  Heart,
  PlusCircle,
  Mail,
  Phone,
} from 'lucide-react';
import { toast } from 'sonner';

const Nonprofits = () => {
  const { user, canManagePipeline } = useAuth();
  const [nonprofits, setNonprofits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
  });

  useEffect(() => {
    loadNonprofits();
  }, []);

  const loadNonprofits = async () => {
    try {
      const response = await nonprofitsAPI.getAll();
      setNonprofits(response.data);
    } catch (error) {
      toast.error('Failed to load nonprofits');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Please enter a nonprofit name');
      return;
    }

    setSaving(true);
    try {
      const response = await nonprofitsAPI.create(formData);
      setNonprofits(prev => [...prev, response.data]);
      setFormData({ name: '', contact_email: '', contact_phone: '' });
      setDialogOpen(false);
      toast.success('Nonprofit partner added successfully');
    } catch (error) {
      toast.error('Failed to create nonprofit');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="nonprofits-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
            SUNRAISING Partners
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage nonprofit organizations in the SUNRAISING program
          </p>
        </div>
        {canManagePipeline() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20" data-testid="add-nonprofit-btn">
                <PlusCircle className="w-4 h-4 mr-2" />
                Add Nonprofit
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-rose-500" />
                  Add Nonprofit Partner
                </DialogTitle>
                <DialogDescription>
                  Add a new nonprofit organization to the SUNRAISING program. 
                  They will receive $500 for each eligible completed sale.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Organization Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Habitat for Humanity"
                      required
                      data-testid="nonprofit-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_email">Contact Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                      placeholder="contact@nonprofit.org"
                      data-testid="nonprofit-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_phone">Contact Phone</Label>
                    <Input
                      id="contact_phone"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                      data-testid="nonprofit-phone-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="save-nonprofit-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Add Partner'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-rose-50 to-amber-50 dark:from-rose-900/20 dark:to-amber-900/20 border-rose-200 dark:border-rose-800">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-rose-100 dark:bg-rose-900/30 rounded-xl">
              <Heart className="w-6 h-6 text-rose-600" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-slate-900 dark:text-white mb-1">
                About SUNRAISING
              </h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                The SUNRAISING program donates <strong className="text-rose-600">$500</strong> to a nonprofit partner 
                for each completed solar installation where the customer opts in. This creates a positive 
                community impact while growing clean energy adoption.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Nonprofits Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Partner Organizations</CardTitle>
          <CardDescription>
            {nonprofits.length} nonprofit{nonprofits.length !== 1 ? 's' : ''} enrolled in SUNRAISING
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {nonprofits.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                    <TableHead>Organization</TableHead>
                    <TableHead>Contact Email</TableHead>
                    <TableHead>Contact Phone</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {nonprofits.map((nonprofit) => (
                    <TableRow key={nonprofit.id} data-testid={`nonprofit-row-${nonprofit.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-rose-100 dark:bg-rose-900/30 rounded-full flex items-center justify-center">
                            <Heart className="w-5 h-5 text-rose-600" />
                          </div>
                          <span className="font-medium text-slate-900 dark:text-white">
                            {nonprofit.name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {nonprofit.contact_email ? (
                          <a
                            href={`mailto:${nonprofit.contact_email}`}
                            className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors"
                          >
                            <Mail className="w-4 h-4" />
                            {nonprofit.contact_email}
                          </a>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {nonprofit.contact_phone ? (
                          <a
                            href={`tel:${nonprofit.contact_phone}`}
                            className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors"
                          >
                            <Phone className="w-4 h-4" />
                            {nonprofit.contact_phone}
                          </a>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-500">
                          {new Date(nonprofit.created_at).toLocaleDateString()}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                          Active
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-16">
              <Heart className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                No nonprofit partners yet
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6">
                Add your first nonprofit partner to start the SUNRAISING program
              </p>
              {canManagePipeline() && (
                <Button
                  onClick={() => setDialogOpen(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Add First Partner
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Nonprofits;
